/**
 * Cookie utility functions for secure API token storage
 */

interface CookieOptions {
  expires?: number; // Days until expiration
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

/**
 * Set a cookie with the given name and value
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  const {
    expires = 30, // Default to 30 days
    secure = window.location.protocol === 'https:',
    sameSite = 'lax'
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (expires) {
    const date = new Date();
    date.setTime(date.getTime() + (expires * 24 * 60 * 60 * 1000));
    cookieString += `; expires=${date.toUTCString()}`;
  }

  cookieString += `; path=/`;
  
  if (secure) {
    cookieString += `; secure`;
  }
  
  cookieString += `; samesite=${sameSite}`;

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  
  return null;
};

/**
 * Delete a cookie by name
 * @param name - Cookie name
 */
export const deleteCookie = (name: string): void => {
  setCookie(name, '', { expires: -1 });
};

/**
 * Check if cookies are available in the browser
 * @returns Whether cookies are supported
 */
export const areCookiesAvailable = (): boolean => {
  try {
    const testCookie = 'test_cookie_support';
    setCookie(testCookie, 'test', { expires: 1 });
    const isSupported = getCookie(testCookie) === 'test';
    deleteCookie(testCookie);
    return isSupported;
  } catch {
    return false;
  }
};

// API Token specific functions
const API_TOKEN_COOKIE_NAME = 'exploregpt_api_token';

/**
 * Store API token in a secure cookie
 * @param token - API token to store
 */
export const storeApiToken = (token: string): void => {
  setCookie(API_TOKEN_COOKIE_NAME, token, {
    expires: 30, // 30 days
    secure: true,
    sameSite: 'strict'
  });
};

/**
 * Retrieve API token from cookie
 * @returns API token or null if not found
 */
export const getApiToken = (): string | null => {
  return getCookie(API_TOKEN_COOKIE_NAME);
};

/**
 * Remove API token from cookie
 */
export const removeApiToken = (): void => {
  deleteCookie(API_TOKEN_COOKIE_NAME);
}; 